import React, { useEffect, useState, useRef } from "react";
import { gsap, Sine } from "gsap";
import { Application, Graphics, Text } from "pixi.js-legacy";
import { CaptchaAnimationProps, GraphicsState } from "../models/Types";
import ChallengeUtils from "./challenges/ChallengeUtils";
import { useTranslation } from "react-i18next";
import { getBackgroundColor, isDarkModeEnabled } from "./Util";

const PARTICLE_COLOR_LIGHT = 0xe7608e;
const PARTICLE_COLOR_DARK = 0xfa528e;

export default function ExplosionAnimation(props: CaptchaAnimationProps) {
  const application: Application = ChallengeUtils.generateApplication({
    width: props.width,
    height: props.height,
    backgroundColor: getBackgroundColor(),
  });

  const { t } = useTranslation();

  const inputRef = useRef<any>(null);

  const [centerXPos, setCenterXPos] = useState(props.width / 2);
  const [centerYPos, setCenterYPos] = useState(props.height / 2);
  const particleColor = isDarkModeEnabled()
    ? PARTICLE_COLOR_DARK
    : PARTICLE_COLOR_LIGHT;

  function drawText() {
    const textMade = new Text("Made by Proton", {
      fontFamily: "Arial",
      fontSize: 10,
      fill: 0x6d4aff,
      align: "center",
    });

    const textInspired = new Text("Inspired by CERN ⚛", {
      fontFamily: "Arial",
      fontSize: 10,
      fill: 0x6d4aff,
      align: "center",
    });

    textMade.x = centerXPos - textMade.getBounds().width / 2;
    textMade.y =
      props.height -
      textMade.getBounds().height -
      textInspired.getBounds().height;
    textInspired.x = centerXPos - textInspired.getBounds().width / 2;
    textInspired.y = props.height - textInspired.getBounds().height;

    textMade.x = Math.round(textMade.x);
    textMade.y = Math.round(textMade.y);
    textInspired.x = Math.round(textInspired.x);
    textInspired.y = Math.round(textInspired.y);

    application.stage.addChild(textMade);
    application.stage.addChild(textInspired);
  }

  function terminate() {
    props.callback();
  }

  function drawCollision(
    geometries: Record<string, Graphics>,
    graphicStates: Record<string, GraphicsState>,
  ) {
    const geometryDefinitions = [
      {
        id: "arc1",
        type: "arc",
        radius: 50,
        stroke: 8,
        startAngle: Math.PI * 0.18,
        endAngle: Math.PI * 0.6,
        color: 0x6d4aff,
      },
      {
        id: "arc2",
        type: "arc",
        radius: 100,
        stroke: 8,
        startAngle: Math.PI * 0.78,
        endAngle: Math.PI * 0.9,
        color: 0x6d4aff,
      },
      {
        id: "arc3",
        type: "arc",
        radius: 60,
        stroke: 3,
        startAngle: Math.PI * 0.98,
        endAngle: Math.PI * 0.1,
        color: 0x372580,
      },
      {
        id: "arc4",
        type: "arc",
        radius: 80,
        stroke: 3,
        startAngle: Math.PI * 0.58,
        endAngle: Math.PI * 0.75,
        color: 0xaa96ff,
      },
      {
        id: "arc5",
        type: "arc",
        radius: 70,
        stroke: 3,
        startAngle: Math.PI * 0.28,
        endAngle: Math.PI * 0.75,
        color: 0x372580,
      },
      {
        id: "arc6",
        type: "arc",
        radius: 70,
        stroke: 3,
        startAngle: -0.1,
        endAngle: 0.6,
        color: 0xaa96ff,
      },
      {
        id: "arc7",
        type: "arc",
        radius: 70,
        stroke: 6,
        startAngle: 3.5,
        endAngle: -1.51,
        color: 0x372580,
      },
      {
        id: "arc8",
        type: "arc",
        radius: 90,
        stroke: 3,
        startAngle: -0.81,
        endAngle: -0.1,
        color: 0x6d4aff,
      },
      {
        id: "arc9",
        type: "arc",
        radius: 100,
        stroke: 4,
        startAngle: 3.6,
        endAngle: -0.6,
        color: 0xaa96ff,
      },
      {
        id: "arc10",
        type: "arc",
        radius: 150,
        stroke: 10,
        startAngle: -0.9,
        endAngle: 0,
        color: 0xecf0f1,
      },
      {
        id: "arc11",
        type: "arc",
        radius: 140,
        stroke: 7,
        startAngle: 1.5,
        endAngle: 2.9,
        color: 0xecf0f1,
      },

      {
        id: "jet1",
        type: "jet",
        radius: 80,
        stroke: 150,
        startAngle: Math.PI * 0.08,
        endAngle: Math.PI * 0.09,
        color: 0x372580,
      },
      {
        id: "jet2",
        type: "jet",
        radius: 80,
        stroke: 150,
        startAngle: -0.6,
        endAngle: -0.57,
        color: 0x6d4aff,
      },
      {
        id: "jet3",
        type: "jet",
        radius: 80,
        stroke: 150,
        startAngle: Math.PI * 0.92,
        endAngle: Math.PI * 0.93,
        color: 0x6d4aff,
      },
      {
        id: "jet5",
        type: "jet",
        radius: 80,
        stroke: 150,
        startAngle: Math.PI * 0.5,
        endAngle: Math.PI * 0.51,
        color: 0x372580,
      },

      { id: "c1", type: "circle", radius: 4, cx: 108, cy: 74, color: 0x6d4aff },
      {
        id: "c2",
        type: "circle",
        radius: 5,
        cx: 216,
        cy: 370,
        color: 0xaa96ff,
      },
      {
        id: "c3",
        type: "circle",
        radius: 3,
        cx: 245,
        cy: 320,
        color: 0x372580,
      },
      {
        id: "c4",
        type: "circle",
        radius: 5,
        cx: 350,
        cy: 350,
        color: 0x6d4aff,
      },
      {
        id: "c5",
        type: "circle",
        radius: 5,
        cx: 355,
        cy: 110,
        color: 0x6d4aff,
      },
      { id: "c6", type: "circle", radius: 2, cx: 40, cy: 200, color: 0x372580 },
      { id: "c7", type: "circle", radius: 5, cx: 50, cy: 238, color: 0x6d4aff },
      {
        id: "c8",
        type: "circle",
        radius: 6,
        cx: 350,
        cy: 300,
        color: 0x372580,
      },
      {
        id: "c9",
        type: "circle",
        radius: 3,
        cx: 354,
        cy: 304,
        color: 0x6d4aff,
      },
    ];

    geometryDefinitions.forEach(function (geom) {
      if (geom.type === "circle") {
        graphicStates[geom.id] = {
          id: geom.id,
          cx: centerXPos,
          cy: centerYPos,
          radius: 0,
          color: geom.color,
          type: geom.type,

          targetCx: geom.cx,
          targetCy: geom.cy,
          targetRadius: geom.radius,
        };
        geometries[geom.id] = new Graphics();

        gsap.to(graphicStates[geom.id], {
          cx: graphicStates[geom.id].targetCx,
          cy: graphicStates[geom.id].targetCy,
          radius: graphicStates[geom.id].targetRadius,
          ease: Sine.easeOut,
          duration: 1,
        });
      } else {
        geometries[geom.id] = new Graphics();
        geometries[geom.id].x = 0;
        geometries[geom.id].y = 0;
        graphicStates[geom.id] = {
          id: geom.id,
          stroke: 0,
          color: geom.color,
          fill: geom.color,
          type: geom.type,
          radius: geom.radius,
          startAngle: geom.startAngle,
          endAngle: geom.startAngle,

          targetEndAngle: geom.endAngle,
          targetRadius: geom.radius,
          targetStroke: geom.stroke,
        };

        if (geom.type === "jet") {
          graphicStates[geom.id].radius = 0;
          gsap.to(graphicStates[geom.id], {
            radius: geom.radius,
            ease: Sine.easeOut,
            duration: 1,
          });
        }

        gsap.to(graphicStates[geom.id], {
          stroke: graphicStates[geom.id].targetStroke,
          endAngle: graphicStates[geom.id].targetEndAngle,
          ease: Sine.easeOut,
          duration: 1,
        });
      }

      application.stage.addChild(geometries[geom.id]);

      geometries["centerExplode"] = new Graphics();
      graphicStates["centerExplode"] = {
        id: "centerExplode",
        type: "centerExplode",
        radius: 0,
        color: particleColor,
        stroke: 3,
        alpha: 1,
        cx: centerXPos,
        cy: centerYPos,
        targetRadius: 200,
        targetAlpha: 0,
      };

      gsap.to(graphicStates["centerExplode"], {
        radius: 200,
        stroke: 0,
        ease: Sine.easeOut,
        duration: 0.9,
      });
      application.stage.addChild(geometries["centerExplode"]);
    });

    setTimeout(terminate, 1100);
  }

  function drawBeams() {
    [{ x: 0 }, { x: props.width - 30 }].forEach((beamInfo) => {
      const beam = new Graphics();
      beam.beginFill(0x6d4aff);
      beam.drawRect(beamInfo.x, centerYPos - 5, 30, 9);
      beam.endFill();
      application.stage.addChild(beam);
    });
  }

  useEffect(() => {
    if (inputRef.current && inputRef.current.children.length == 0) {
      const view = inputRef.current;
      view.appendChild(application.view);

      let collided = false;
      const geometries: Record<string, Graphics> = {};
      const graphicStates: Record<string, GraphicsState> = {};

      setCenterXPos(props.width / 2);
      setCenterYPos(props.height / 2);

      const particleLeft = new Graphics();
      particleLeft.x = 0;
      particleLeft.y = centerYPos;
      particleLeft.beginFill(particleColor);
      particleLeft.drawCircle(0, -0.7, 4);
      particleLeft.endFill();
      application.stage.addChild(particleLeft);

      const particleRight = new Graphics();
      particleRight.beginFill(particleColor);
      particleRight.x = props.width;
      particleRight.y = centerYPos;
      particleRight.drawCircle(0, -0.7, 4);
      particleRight.endFill();
      application.stage.addChild(particleRight);

      drawText();
      drawBeams();

      application.ticker.add((delta) => {
        delta *= 4;

        if (!collided) {
          particleLeft.x += delta;
          particleRight.x -= delta;

          if (particleLeft.x > particleRight.x) {
            particleLeft.x = particleRight.x = centerXPos;
            collided = true;
            drawCollision(geometries, graphicStates);
          }
        } else {
          particleLeft.clear();
          particleRight.clear();
        }

        Object.keys(graphicStates).forEach(function (gkey) {
          // Maybe instead of updating everything with tweening, we do the update here?

          geometries[gkey].clear();
          const _state = graphicStates[gkey];
          if (_state.type === "centerExplode") {
            geometries[gkey].lineStyle(_state.stroke || 0, _state.color, 1);
            geometries[gkey].drawCircle(
              _state.cx || 0,
              _state.cy || 0,
              _state.radius,
            );
          } else if (_state.type !== "circle") {
            ChallengeUtils.drawArc(
              geometries[gkey],
              centerXPos,
              centerYPos,
              _state.radius,
              _state.stroke || 0,
              _state.color,
              _state.startAngle,
              _state.endAngle,
            );
          } else {
            geometries[gkey].beginFill(_state.color);
            geometries[gkey].drawCircle(
              _state.cx || 0,
              _state.cy || 0,
              _state.radius,
            );
            geometries[gkey].endFill();
          }
        });
      });
    }
  }, [inputRef.current]);

  return (
    <div className={"text-left"}>
      <h1>{t("VerificationSuccessful.text")}</h1>
      <div ref={inputRef}></div>
    </div>
  );
}
